import React, { useEffect, useRef, useState, useMemo } from "react";
import { Grid, Typography, TextField, Breadcrumbs } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import Layout from "../../layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { config, weeklyPlanAPI } from "../../config";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { MaterialReactTable } from "material-react-table";
import MuiLink from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import weekday from "dayjs/plugin/weekday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

function WeeklyPlanSchedule() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  dayjs.extend(weekday);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);

  const saturday = dayjs().weekday(6);
  const monday = dayjs().weekday(1); // Monday of this week

  const [fromDate, setFromDate] = React.useState(monday);
  const [toDate, setToDate] = React.useState(saturday);

  const [totalCount, setTotalCount] = useState(0);

  const [weeklyPlan, setWeeklyPlan] = useState("");

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  const getWeeklyReport = () => {
    const data = {
      pageSize,
      pageIndex,
      FromDate: fromDate,
      ToDate: toDate,
      intType: 6,
      sorting,
      columnFilters,
    };

    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = weeklyPlanAPI;

    axios(config)
      .then(function (response) {
        setTotalCount(response.data.totalCount);
        setWeeklyPlan(response.data.data);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    getWeeklyReport(
      pageIndex,
      pageSize,
      fromDate,
      toDate,
      sorting,
      columnFilters
    );
  }, [pageIndex, pageSize, fromDate, toDate, sorting, columnFilters]);

  useEffect(() => {
    if (id !== undefined) {
      getWeeklyReport();
    }
  }, [id]);

  const columns = useMemo(
    () => [
      {
        header: "S.No.",
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          return pageIndex * pageSize + row.index + 1;
        },
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "employee_name",
        header: "Employee Name",
        enableSorting: false,
      },
      {
        accessorKey: "start_date",
        header: "Start Date",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? new Date(date).toLocaleDateString("en-GB") : "";
        },
      },
      {
        accessorKey: "end_date",
        header: "End Date",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? new Date(date).toLocaleDateString("en-GB") : "";
        },
      },

      {
        accessorKey: "employee_id",
        header: "View",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            onClick={() => {
              const employee_id = row.original.employee_id;
              const queryParams = new URLSearchParams({
                from: fromDate?.toISOString?.(),
                to: toDate?.toISOString?.(),
              });
              navigate(
                `/weeklyPlanScheduleView/${employee_id}?${queryParams.toString()}`
              );
            }}
          >
            <VisibilityIcon sx={{ color: "green" }} />
          </IconButton>
        ),

        size: 50,
      },
    ],
    [navigate, fromDate, toDate]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Typography className="page_title" textAlign="left">
              Weekly Plan Schedule
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <MuiLink underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </MuiLink>
              <Typography color="text.primary">Reports</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            container
            item
            xs={12}
            lg={6}
            justifyContent="flex-end"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
              >
                Back
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ margin: "15px 0", padding: "10px 15px 0 0" }}
        >
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select From Date"
                value={fromDate}
                inputFormat="DD/MM/YYYY"
                maxDate={toDate || undefined}
                onChange={(newValue) => {
                  if (!newValue) return;

                  const selectedDay = dayjs(newValue);
                  const dayOfWeek = selectedDay.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

                  // If not Monday, set to the Monday of that week
                  const adjustedDate =
                    dayOfWeek === 1 ? selectedDay : selectedDay.weekday(1);
                  setFromDate(adjustedDate);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select To Date"
                value={toDate}
                inputFormat="DD/MM/YYYY"
                minDate={fromDate || undefined}
                onChange={(newValue) => {
                  if (!newValue) return;

                  const selectedDay = dayjs(newValue);
                  const dayOfWeek = selectedDay.day(); // 0 = Sunday, 6 = Saturday

                  // If not Saturday, set to Saturday of that week
                  const adjustedDate =
                    dayOfWeek === 6 ? selectedDay : selectedDay.weekday(6);
                  setToDate(adjustedDate);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={weeklyPlan}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          className="mat-react-tbl"
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters,
          }}
          manualPagination
          manualSorting
          manualFiltering
          rowCount={totalCount}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });

            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important", // selected text color
              },
              "& .MuiSelect-icon": {
                color: "#fff !important", // dropdown arrow color
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important", // Change text color
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important", // Change underline color
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important", // Hover underline color
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important", // Focused underline color
              },
            },
          }}
        />
      </Grid>
    </Layout>
  );
}

export default WeeklyPlanSchedule;
